import type { ReactSelectOption } from "~/components/reusable/Select"

export const HAKIO_EVALUATION_VALUE = "Ensemble_Weighted" as const
export const HAKIO_EVALUATION_RANGES = [
  "last_month",
  "last_3_months",
  "last_6_months",
  "last_year",
] as const
export const BASELINE_VALUES = ["baseline_seasonal", "baseline_moving_average"] as const
export type BaselineType = (typeof BASELINE_VALUES)[number]

export const NPI_HAKIO_EVALUATION_VALUE = "best_npi_model" as const
export const NPI_HAKIO_EVALUATION_RANGES = ["first_13_weeks", "first_26_weeks"] as const
export const NPI_BASELINE_VALUES = ["NPIBaseline0"] as const

export const baselineOptions: ReactSelectOption<BaselineType>[] = [
  {
    label: "Baseline seasonal",
    value: "baseline_seasonal",
  },
  {
    label: "Baseline moving average",
    value: "baseline_moving_average",
  },
]

export enum ForecastEvaluationColumn {
  ForecastQuality = "forecastQuality",
  Forecast = "forecast",
  Sales = "sales",
  Difference = "difference",
  DifferenceBaseline = "differenceBaseline",
  DifferencePercentage = "differencePercentage",
  DifferenceBaselinePercentage = "differenceBaselinePercentage",
  Baseline = "baseline",
  AbsoluteError = "absoluteError",
  BaselineAbsoluteError = "baselineAbsoluteError",
  AbsoluteErrorPercentage = "absoluteErrorPercentage",
  BaselineAbsoluteErrorPercentage = "baselineAbsoluteErrorPercentage",
  OverstockImprovement = "overstockImprovement",
  UnderstockImprovement = "understockImprovement",
  // Intermediate columns used in calculations
  Overstock = "overstock",
  BaselineOverstock = "baselineOverstock",
  Understock = "understock",
  BaselineUnderstock = "baselineUnderstock",
}

export enum ForecastQualityLevel {
  Low = "red",
  Moderate = "yellow",
  High = "green",
}

export const FORECAST_QUALITY_THRESHOLDS = {
  LOW_TO_MODERATE: 25,
  MODERATE_TO_HIGH: 50,
}

export const FORECAST_QUALITY_DESCRIPTIONS = {
  [ForecastQualityLevel.Low]:
    "Low forecast quality due to factors such as high volatility, insufficient historical data, or irregular sales patterns.",
  [ForecastQualityLevel.Moderate]:
    "Moderate forecast quality. Some factors like seasonal variations or changing trends may affect accuracy.",
  [ForecastQualityLevel.High]:
    "High forecast quality with consistent patterns and sufficient historical data.",
}

export const getForecastQualityLevel = (quality: number): ForecastQualityLevel => {
  if (quality < FORECAST_QUALITY_THRESHOLDS.LOW_TO_MODERATE) {
    return ForecastQualityLevel.Low
  } else if (quality < FORECAST_QUALITY_THRESHOLDS.MODERATE_TO_HIGH) {
    return ForecastQualityLevel.Moderate
  } else {
    return ForecastQualityLevel.High
  }
}

export const FORECAST_QUALITY_LABELS: Record<ForecastQualityLevel, string> = {
  [ForecastQualityLevel.Low]: "Low",
  [ForecastQualityLevel.Moderate]: "Moderate",
  [ForecastQualityLevel.High]: "High",
}

/** A variant's bucket label from its stored `overallScore` (0-1, as ForecastVariantQuality keeps it). */
export const getForecastQualityLabel = (overallScore: number): string =>
  FORECAST_QUALITY_LABELS[getForecastQualityLevel(overallScore * 100)]

/** Average a group's variant scores (0-100) and return its bucket label, or null. */
export const getAggregateForecastQualityLabel = (
  scores: (number | null | undefined)[],
): string | null => {
  const valid = scores.filter((s): s is number => s != null)
  if (valid.length === 0) return null
  const avg = valid.reduce((a, b) => a + b, 0) / valid.length
  return FORECAST_QUALITY_LABELS[getForecastQualityLevel(avg)]
}
